import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Images, RefreshCw, FileText, ArrowLeft } from "lucide-react";
import {
  connectorsAPI,
  FileUsageReport,
  FileUsageFile,
  FileUsageUnregistered,
  FileUsageReference,
} from "../api/connectorsApi";
import { resolveConnectorAssetPublicUrl } from "../utils/pictureUtils";

/**
 * Read-only report of the remote server's uploaded files: which are still
 * referenced by live content ("in use") and which are orphaned. The remote
 * uploads folder keeps every file ever uploaded — replacing a picture or PDF
 * only overwrites the field value — so orphans accumulate invisibly.
 */

type FilterTab = "all" | "in-use" | "orphaned" | "unregistered";

const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico"]);

export function ConnectorFilesPage() {
  const { key = "" } = useParams<{ key: string }>();
  const [report, setReport] = useState<FileUsageReport | null>(null);
  const [assetBase, setAssetBase] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usage, conn] = await Promise.all([
        connectorsAPI.fileUsage(key),
        connectorsAPI.get(key),
      ]);
      setReport(usage.report);
      const configured = conn.connector.config?.assetPublicUrl;
      setAssetBase(
        resolveConnectorAssetPublicUrl({
          connectorAssetPublicUrl: typeof configured === "string" ? configured : undefined,
          source: { connectorKey: key },
        }) || ""
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load file usage report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const visibleFiles = useMemo(() => {
    if (!report) return [];
    let files = report.files;
    if (tab === "in-use") files = files.filter((f) => f.status === "in-use");
    if (tab === "orphaned") files = files.filter((f) => f.status === "orphaned");
    if (tab === "unregistered") return [];
    const q = search.trim().toLowerCase();
    if (q) {
      files = files.filter(
        (f) =>
          f.filename.toLowerCase().includes(q) ||
          f.name.toLowerCase().includes(q) ||
          f.gallery.toLowerCase().includes(q)
      );
    }
    return files;
  }, [report, tab, search]);

  const visibleUnregistered = useMemo(() => {
    if (!report || tab !== "unregistered") return [];
    const q = search.trim().toLowerCase();
    if (!q) return report.unregistered;
    return report.unregistered.filter((f) => f.filename.toLowerCase().includes(q));
  }, [report, tab, search]);

  if (!key) {
    return <Alert variant="destructive"><AlertDescription>No connector key in URL.</AlertDescription></Alert>;
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <Link to="/admin/connectors" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="w-4 h-4 mr-1" /> Connections
        </Link>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Images className="w-5 h-5 mr-2" /> Files on {key}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && <p className="text-sm text-gray-500">Scanning remote collections…</p>}

          {report && !loading && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryStat label="Registered files" value={report.summary.inventoryTotal} />
                <SummaryStat label="In use" value={report.summary.inUse} tone="ok" />
                <SummaryStat label="Orphaned" value={report.summary.orphaned} tone="warn" />
                <SummaryStat label="Unregistered refs" value={report.summary.unregisteredReferences} />
              </div>

              <p className="text-xs text-gray-500">
                Registry: <code>{report.inventoryCollection}</code> — scanned:{" "}
                {report.scanCollections.map((c) => <code key={c} className="mr-1">{c}</code>)}.
                Files uploaded directly into item fields (post pictures, publication PDFs, CVs) bypass the
                registry and appear under “Unregistered refs”. Files on disk that were never registered and
                are no longer referenced anywhere cannot be detected from here.
              </p>

              {report.scanErrors.length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    Some collections could not be scanned:{" "}
                    {report.scanErrors.map((e) => `${e.collection} (${e.error})`).join("; ")}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <FilterButton current={tab} value="all" onSelect={setTab} label={`All (${report.summary.inventoryTotal})`} />
                <FilterButton current={tab} value="in-use" onSelect={setTab} label={`In use (${report.summary.inUse})`} />
                <FilterButton current={tab} value="orphaned" onSelect={setTab} label={`Orphaned (${report.summary.orphaned})`} />
                <FilterButton current={tab} value="unregistered" onSelect={setTab} label={`Unregistered (${report.summary.unregisteredReferences})`} />
                <Input
                  className="ml-auto w-56"
                  placeholder="Search filename, name, gallery…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {tab !== "unregistered" ? (
                <FileTable files={visibleFiles} assetBase={assetBase} />
              ) : (
                <UnregisteredTable files={visibleUnregistered} assetBase={assetBase} />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryStat({ label, value, tone }: { label: string; value: number; tone?: "ok" | "warn" }) {
  const color = tone === "ok" ? "text-green-700" : tone === "warn" ? "text-amber-600" : "text-gray-900";
  return (
    <div className="border rounded-md p-3">
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function FilterButton({
  current,
  value,
  label,
  onSelect,
}: {
  current: FilterTab;
  value: FilterTab;
  label: string;
  onSelect: (tab: FilterTab) => void;
}) {
  return (
    <Button variant={current === value ? "default" : "outline"} size="sm" onClick={() => onSelect(value)}>
      {label}
    </Button>
  );
}

function Thumb({
  filename,
  extension,
  assetBase,
  forceImage = false,
}: {
  filename: string;
  extension: string;
  assetBase: string;
  /** Registry entries are always images, even when the filename has no recognized extension. */
  forceImage?: boolean;
}) {
  const isImage = forceImage || IMAGE_EXTS.has(extension);
  const url = assetBase
    ? `${assetBase}/${isImage ? "img" : "pdf"}/${encodeURIComponent(filename)}`
    : "";
  if (!url) return <div className="w-12 h-12 rounded bg-gray-100" />;
  if (!isImage) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center text-gray-500" title={filename}>
        <FileText className="w-5 h-5" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" title={filename}>
      <img src={url} alt={filename} loading="lazy" className="w-12 h-12 rounded object-cover bg-gray-100" />
    </a>
  );
}

const REFS_COLLAPSED_COUNT = 4;

function ReferencedByList({ refs }: { refs: FileUsageReference[] }) {
  const [expanded, setExpanded] = useState(false);
  if (!refs.length) return <span className="text-xs text-gray-400">—</span>;
  const visible = expanded ? refs : refs.slice(0, REFS_COLLAPSED_COUNT);
  const hidden = refs.length - visible.length;
  return (
    <ul className="text-xs space-y-0.5">
      {visible.map((r, i) => (
        <li key={`${r.collection}-${r.uid}-${r.via}-${i}`}>
          <span className="font-medium">{r.collection}</span>
          {r.title ? <> · {r.title}</> : null}
          <span className="text-gray-400"> ({r.via})</span>
        </li>
      ))}
      {(hidden > 0 || expanded) && (
        <li>
          <button
            type="button"
            className="text-blue-600 hover:underline"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Show less" : `+${hidden} more`}
          </button>
        </li>
      )}
    </ul>
  );
}

function FileTable({ files, assetBase }: { files: FileUsageFile[]; assetBase: string }) {
  if (!files.length) return <p className="text-sm text-gray-500">No files match.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b">
            <th className="py-2 pr-2 w-14"></th>
            <th className="py-2 pr-2">Filename</th>
            <th className="py-2 pr-2">Name</th>
            <th className="py-2 pr-2">Gallery</th>
            <th className="py-2 pr-2">Status</th>
            <th className="py-2">Referenced by</th>
          </tr>
        </thead>
        <tbody>
          {files.map((f) => (
            <tr key={f.filename} className="border-b last:border-0 align-top">
              <td className="py-2 pr-2"><Thumb filename={f.filename} extension={f.extension} assetBase={assetBase} forceImage /></td>
              <td className="py-2 pr-2 font-mono text-xs break-all">{f.filename}</td>
              <td className="py-2 pr-2">{f.name || <span className="text-gray-400">—</span>}</td>
              <td className="py-2 pr-2">{f.gallery || <span className="text-gray-400">—</span>}</td>
              <td className="py-2 pr-2">
                <Badge variant={f.status === "in-use" ? "default" : "destructive"}>
                  {f.status === "in-use" ? "In use" : "Orphaned"}
                </Badge>
              </td>
              <td className="py-2"><ReferencedByList refs={f.referencedBy} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UnregisteredTable({ files, assetBase }: { files: FileUsageUnregistered[]; assetBase: string }) {
  if (!files.length) return <p className="text-sm text-gray-500">No unregistered references.</p>;
  return (
    <div className="overflow-x-auto">
      <p className="text-xs text-gray-500 mb-2">
        Referenced by content but missing from the upload registry — uploaded directly into item fields.
        These files ARE in use; they are listed so the registry gap is visible.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b">
            <th className="py-2 pr-2 w-14"></th>
            <th className="py-2 pr-2">Filename</th>
            <th className="py-2 pr-2">Type</th>
            <th className="py-2">Referenced by</th>
          </tr>
        </thead>
        <tbody>
          {files.map((f) => (
            <tr key={f.filename} className="border-b last:border-0 align-top">
              <td className="py-2 pr-2"><Thumb filename={f.filename} extension={f.extension} assetBase={assetBase} /></td>
              <td className="py-2 pr-2 font-mono text-xs break-all">{f.filename}</td>
              <td className="py-2 pr-2 uppercase text-xs text-gray-500">{f.extension || "?"}</td>
              <td className="py-2"><ReferencedByList refs={f.referencedBy} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
